import { cf2imgix } from '@uswitch/ev-components/lib/components/elements/imgix-img'
import React, { Fragment, useState, useEffect } from 'react'
import { useQuery } from 'urql'

import FormContainer from '../../landing-form/form-container'
import LandingForm from '../../landing-form/form-types/standard'
import MetaData from '../../meta-data/meta-data'
import { getAddressDetails } from '../results'
import { MS3_PROVIDERS } from '~/components/pages/hull/autosuggest-providers'
import { useAppContext } from '~/contexts/app-context'
import { usePageContext } from '~/contexts/page-context'
import { sendFbView } from '~/critical/utils/track'
import CUSTOMER_QUERY from '~/graphql/queries/customer-data'
import { HULL_RESULTS_PATH } from '~/routes-list'

import { AltNetModal } from './components/modal'
import { ProviderSection } from './sections/provider-section'
import SmallCopySection from './sections/small-copy-section'
import { StepCards } from './sections/step-cards'
import { USPCardSection } from './sections/usp-cards'

const StripeSVG = () => (
  <svg width='153' height='10' viewBox='0 0 153 10' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M151 2L151.093 3.99786C152.186 3.94721 153.036 3.02656 152.999 1.93231C152.962 0.838061 152.052 -0.0229971 150.957 0.000459075L151 2ZM21.4409 8L21.3484 6.00214C20.263 6.05241 19.4163 6.96002 19.4414 8.04627C19.4666 9.13252 20.3543 10 21.4409 10V8ZM134.467 10C135.572 10 136.467 9.10457 136.467 8C136.467 6.89543 135.572 6 134.467 6V10ZM1.04285 7.21383L151.043 3.99954L150.957 0.000459075L0.957153 3.21474L1.04285 7.21383ZM150.907 0.00214124L21.3484 6.00214L21.5334 9.99786L151.093 3.99786L150.907 0.00214124ZM21.4409 10H134.467V6H21.4409V10Z'
      fill='white'
    />
  </svg>
)

const Hull = () => {
  const { comparisonId, isPersonalisedResults } = useAppContext()
  const { nonServiceabilityPage } = usePageContext()
  const [showModal, setShowModal] = useState(false)

  // This query will only run if a user has a personalised cookie saved and if the page is serviceable
  const [{ data: customerData }, reexecuteCustomerQuery] = useQuery({
    query: CUSTOMER_QUERY,
    requestPolicy: 'cache-first',
    variables: { comparisonId },
    pause: !isPersonalisedResults && !nonServiceabilityPage
  })

  const getCustomer = customerData?.getCustomer || {}
  const { currentProvider, address } = getAddressDetails(getCustomer)

  useEffect(() => {
    sendFbView()
  }, [])

  return (
    <Fragment>
      <MetaData
        metaTitle='Broadband Deals In Hull - Uswitch'
        metaDescription='Compare broadband deals in Hull. Find the best internet providers and the fastest and cheapest deals in your area.'
      />
      <div>
        <div sx={{ backgroundColor: '#FF869C', py: ['32px', '32px', '64px'] }}>
          <div
            sx={{
              display: 'flex',
              flexDirection: ['column', 'column', 'row'],
              px: [16, '100px'],
              maxWidth: 1226,
              margin: '0 auto',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div sx={{ width: '100%', mt: ['32px', '32px', '32px'] }}>
              <h1
                sx={{
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: ['36px', '36px', '48px'],
                  lineHeight: ['42px', '42px', '56px'],
                  color: '#141414'
                }}
              >
                Get faster broadband{' '}
                <span sx={{ position: 'relative' }}>
                  <span sx={{ position: 'relative', display: 'inline-block', zIndex: 1 }}>for less</span>
                  <span
                    sx={{
                      zIndex: 0,
                      position: 'absolute',
                      top: ['20px', '20px', '25px'],
                      right: ['-10px', '-10px', '6px']
                    }}
                  >
                    <StripeSVG />
                  </span>
                </span>{' '}
                in Hull
              </h1>
              <p
                sx={{
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: ['16px', '16px', '18px'],
                  lineHeight: '28px'
                }}
              >
                Upgrade your broadband and downsize your bills today. Save up to £22 per month* with a new deal.
              </p>
            </div>

            <FormContainer
              customCss={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                mt: 0,
                ml: 0,
                mr: 0,
                '.form-col': {
                  width: '100%',
                  maxWidth: '400px',
                  margin: '0 auto',
                  '#landing-form': {
                    borderRadius: '8px'
                  }
                },
                '.input-form': {
                  width: ['100%', 'inherit']
                }
              }}
            >
              <h3
                sx={{
                  color: '#141414',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: ['20px', '20px', '24px'],
                  lineHeight: '32px',
                  mb: [12, 6]
                }}
              >
                Check your options now
              </h3>
              <LandingForm
                redirect={HULL_RESULTS_PATH}
                customerAddress={address}
                onUpdate={() => {
                  reexecuteCustomerQuery({ requestPolicy: 'network-only' })
                }}
                showProviderBox={true}
                showProviderBoxAfterPostcode
                provider={currentProvider}
                buttonText={'Find deals'}
                isFetchingAddress={false}
                providersOverride={MS3_PROVIDERS}
              />
            </FormContainer>
          </div>
        </div>
      </div>
      <div sx={{ bg: 'black' }}>
        <img
          alt='humber bridge'
          sx={{
            width: '100%',
            maxWidth: '2000px',
            m: '0 auto',
            height: ['150px', 'auto'],
            objectFit: ['initial', 'contain']
          }}
          src={cf2imgix(
            '//images.ctfassets.net/n36b8lzdmgnp/7CratWQ3qGtElMAUb65Zt3/3db3af4b0bc881c6634eb6a7026cd120/stock-photo-humber-bridge-from-barton-yorkshire-795676273.png?h=250'
          )}
        />
      </div>

      {showModal && (
        <div
          sx={{
            '> div': { display: showModal ? 'flex' : 'none' },
            '> div > div': {
              m: '0 auto'
            },
            '> div > div > form': {
              padding: 0
            }
          }}
        >
          <AltNetModal
            setShowModal={setShowModal}
            address={address}
            currentProvider={currentProvider}
            reexecuteCustomerQuery={reexecuteCustomerQuery}
          />
        </div>
      )}

      <USPCardSection />
      <ProviderSection setShowModal={setShowModal} />
      <StepCards />
      <SmallCopySection />
    </Fragment>
  )
}

export default Hull
